import type { CareNavigationRosterMember, CareNavigationRosterResponse } from "@t3tools/contracts";
import { DataTable, type DataTableColumn } from "./dashboardKit";

const columns: Array<DataTableColumn<CareNavigationRosterMember>> = [
  { key: "name", label: "Name", sortable: true },
  { key: "title", label: "Role", sortable: true, render: (member) => member.title ?? "—" },
  {
    key: "managerName",
    label: "Reports to",
    sortable: true,
    render: (member) => member.managerName ?? member.managerEmail ?? "Not recorded",
  },
  {
    key: "status",
    label: "Account status",
    sortable: true,
    render: (member) =>
      member.status === "active"
        ? "Active"
        : member.status === "suspended"
          ? "Suspended"
          : member.status === "archived"
            ? "Archived"
            : "Inactive",
  },
  {
    key: "navigatorName",
    label: "Carespaces account",
    sortable: true,
    render: (member) => member.navigatorName ?? "No unique email match",
  },
];

export function CareNavigationRoster(props: {
  roster: CareNavigationRosterResponse;
  members: readonly CareNavigationRosterMember[];
}) {
  const active = props.roster.members.filter((member) => member.status === "active").length;
  const unmatched = props.members.filter((member) => !member.navigatorUserId);
  return (
    <>
      {unmatched.length ? (
        <p
          role="status"
          className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm"
        >
          Metrics omit staff without a unique Carespaces email match:{" "}
          {unmatched.map((member) => member.name).join(", ")}.
        </p>
      ) : null}
      <details className="rounded-lg border border-border/70 bg-background p-4">
        <summary className="cursor-pointer text-sm font-semibold">
          Team roster · {active} active · {props.roster.members.length - active} inactive
        </summary>
        <p className="my-3 text-xs text-muted-foreground">
          Care Navigation department. Teams include each manager and their reporting chain. Account
          status and reporting lines are current as of{" "}
          {new Date(props.roster.syncedAt).toLocaleString("en-US", { timeZone: "UTC" })} UTC,
          including when viewing past months. Suspension indicates a disabled account; it does not
          confirm a departure or departure date. Staff without a unique Carespaces email match have
          no attributed metrics. Older snapshot rows without an unambiguous account match are
          excluded.
        </p>
        <DataTable columns={columns} rows={[...props.members]} rowKey={(member) => member.email} />
        {props.roster.unmatchedNavigatorNames.length ? (
          <p role="status" className="mt-3 text-xs text-muted-foreground">
            Assigned accounts outside the matched Care Navigation directory are excluded:{" "}
            {props.roster.unmatchedNavigatorNames.join(", ")}.
          </p>
        ) : null}
      </details>
    </>
  );
}
